import { useState, useContext, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Divider,
  Paper,
  Link,
  useTheme,
  Button,
  CircularProgress
} from '@mui/material';
import {
  AccessTime,
  TaskAlt,
  Close,
  Warning,
  Report,
  InfoOutlined,
  Celebration,
  LocalPolice,
  AssistantPhoto,
  FavoriteBorder,
} from '@mui/icons-material';
import UserLogIn from './UserLogIn';
import AdminLogIn from './AdminLogIn';
import { ReactComponent as BlueNoteLogo } from '../assets/logo_bluenote.svg';
import SettingsFieldDisplay from './tool/SettingsFieldDisplay';
import CguContent from './tool/CguContent';
import { ThemeContext } from './Context';

const iconComponents = {
  accesstime: AccessTime,
  taskalt: TaskAlt,
  close: Close,
  warning: Warning,
  report: Report,
  infoutline: InfoOutlined,
  celebration: Celebration,
  localpolice: LocalPolice,
  assistantphoto: AssistantPhoto,
  favoriteborder: FavoriteBorder,
};

function LogInPage() {
  const theme = useTheme();
  const { theme: themeMode } = useContext(ThemeContext);
  const this_year = new Date().getFullYear();
  const [alertData, setAlertData] = useState(null);
  const [viewMode, setViewMode] = useState('login');
  const [cguLang, setCguLang] = useState('fr');
  const [cguEntries, setCguEntries] = useState([]);
  const [guardiaOn, setGuardiaOn] = useState(false);
  const [isCguLoading, setIsCguLoading] = useState(false);
  const [isUserCguGateActive, setIsUserCguGateActive] = useState(false);

  // Récupérer les données d'alerte
  useEffect(() => {
    const fetchAlert = async () => {
      try {
        const response = await fetch('/api/alert');
        if (response.ok) {
          const data = await response.json();
          setAlertData(data);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des alertes:', error);
      }
    };

    fetchAlert();
  }, []);

  const openCguInline = async (lang) => {
    setCguLang(lang);
    setViewMode('cgu');

    if (cguEntries.length > 0) {
      return;
    }

    setIsCguLoading(true);
    try {
      const [cguResponse, guardiaResponse] = await Promise.all([
        fetch('/api/cgu'),
        fetch('/api/guardiasettings/public-status')
      ]);

      if (cguResponse.ok) {
        const cguData = await cguResponse.json();
        setCguEntries(Array.isArray(cguData) ? cguData : []);
      }

      if (guardiaResponse.ok) {
        const guardiaData = await guardiaResponse.json();
        setGuardiaOn(Boolean(guardiaData?.guardiaOn));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des CGU:', error);
    } finally {
      setIsCguLoading(false);
    }
  };

  // Obtenir le composant d'icône
  const getIconComponent = (iconName) => {
    const IconComponent = iconComponents[iconName] || InfoOutlined;
    return IconComponent;
  };

  const getCguContentByVersionAndLang = (version, lang) => {
    const entry = cguEntries.find((item) => item.version === version && item.lang === lang);
    return entry?.content || '';
  };

  const currentBlueNoteContent = getCguContentByVersionAndLang('bluenote', cguLang);
  const currentGuardiaContent = guardiaOn ? getCguContentByVersionAndLang('guardia', cguLang) : '';
  const cguTitle = cguLang === 'fr'
    ? "Conditions d'utilisation et mentions légales"
    : 'Gebruiksvoorwaarden en wettelijke vermeldingen';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: '#f8f9fa',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(30deg, transparent 20%, rgba(25, 118, 210, 0.1) 40%, transparent 60%), linear-gradient(150deg, transparent 40%, rgba(25, 118, 210, 0.08) 50%, transparent 60%), linear-gradient(270deg, transparent 10%, rgba(25, 118, 210, 0.12) 45%, transparent 90%)',
          zIndex: 0
        }
      }}
    >
      {/* Conteneur principal avec les boîtes */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: 3,
          position: 'relative', 
          zIndex: 1,
          minHeight: '100vh',
          px: 2
        }}
      >
        <Container component="main" maxWidth={viewMode === 'cgu' ? 'md' : 'sm'} sx={{ margin: 0 }}>
          <Paper 
            elevation={3} 
            sx={{
              p: 4,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.67)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}
          >
            {viewMode === 'login' ? (
              <>
                {!isUserCguGateActive && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 4
                    }}
                  >
                    <BlueNoteLogo
                      style={{
                        width: '30px',
                        height: 'auto',
                        marginBottom: theme.spacing(2),
                        color: theme.palette.primary.main,
                      }}
                    />
                    <Typography
                      variant="h4"
                      component="h1"
                      fontWeight="bold"
                      sx={{
                        color: theme.palette.primary.main,
                        mb: 0.2
                      }}
                    >
                      BlueNote
                    </Typography>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        color: theme.palette.text.secondary,
                        mb: 0.2
                      }}
                    >
                      <SettingsFieldDisplay fieldName="clientName" />
                    </Typography>
                  </Box>
                )}

                {/* Formulaire de connexion utilisateur */}
                <UserLogIn onCguGateChange={setIsUserCguGateActive} />

                {!isUserCguGateActive && (
                  <Divider
                    sx={{
                      my: 3,
                      backgroundColor: theme.palette.divider
                    }}
                  />
                )}

                {/* Informations sur l'application */}
                {!isUserCguGateActive && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                    <Link href="https://pyll.be/bluenote" target="_blank" underline="hover"><b>BlueNote</b></Link> - v. <SettingsFieldDisplay fieldName="version" /> &copy;{this_year}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                    En accédant à cette application, vous acceptez les <Link onClick={() => openCguInline('fr')} underline="hover" sx={{ cursor: 'pointer' }}>conditions d'utilisation et mentions légales</Link>.
                    {' '}
                    Door deze applicatie te gebruiken, aanvaardt u de <Link onClick={() => openCguInline('nl')} underline="hover" sx={{ cursor: 'pointer' }}>gebruiksvoorwaarden en wettelijke vermeldingen</Link>.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, textAlign: 'center' }}>
                    Proudly made in Belgium by<br />
                    <a
                      href="https://pyll.be/bluenote"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        background: themeMode === 'dark'
                          ? 'linear-gradient(135deg,rgb(68, 167, 233),rgb(83, 139, 196))'
                          : 'linear-gradient(135deg, #667eea, #1a253c)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        transition: 'all 0.2s ease-in-out',
                        display: 'inline-block',
                        lineHeight: '1.6',
                        padding: '2px 0',
                        backgroundSize: '100% 100%'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                    >
                      <i style={{ fontSize: '2em', lineHeight: 'inherit' }}>pyll</i> <b style={{ fontSize: '0.9em', lineHeight: 'inherit' }}>SRL</b>
                    </a>
                  </Typography>
                </Box>
                )}

                {/* Accès administrateur */}
                {!isUserCguGateActive && <AdminLogIn />}
              </>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={() => setViewMode('login')}>
                    Retour
                  </Button>
                  <Button variant="contained" onClick={() => setCguLang(cguLang === 'fr' ? 'nl' : 'fr')}>
                    {cguLang === 'fr' ? 'Nederlands' : 'Français'}
                  </Button>
                </Box>

                <Typography variant="h5" component="h2" sx={{ mb: 2, color: theme.palette.primary.main }}>
                  {cguTitle}
                </Typography>

                {isCguLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1 }}>
                    {currentBlueNoteContent ? (
                      <CguContent content={currentBlueNoteContent} />
                    ) : (
                      <Typography variant="body2">
                        {cguLang === 'fr' ? 'Aucun contenu disponible.' : 'Geen inhoud beschikbaar.'}
                      </Typography>
                    )}
                    {guardiaOn && currentGuardiaContent && (
                      <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography
                          variant="overline"
                          sx={{
                            display: 'block',
                            mb: 1,
                            fontWeight: 700,
                            letterSpacing: 1,
                            color: 'text.secondary',
                          }}
                        >
                          {cguLang === 'fr'
                            ? 'Module additionnel GuardIA'
                            : 'Aanvullende module GuardIA'}
                        </Typography>
                        <CguContent content={currentGuardiaContent} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Container>

              {/* Encadré d'alerte - FR/NL affiché si show_alert est true */}
        {viewMode === 'login' && alertData?.show_alert && (
          <Box sx={{ width: '350px', minWidth: '300px' }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.67)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                height: 'fit-content'
              }}
            >
                {/* En-tête de l'alerte avec icône */}
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  gap: 1
                }}
              >
                {(() => {
                  const IconComponent = getIconComponent(alertData.alert_icon);
                  return (
                    <IconComponent 
                      sx={{ 
                        color: theme.palette.primary.main,
                        fontSize: '2.5rem'
                      }} 
                    />
                  );
                })()}
                <Typography 
                  variant="h6" 
                  component="h2" 
                  sx={{ 
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    flexGrow: 1
                  }}
                >
                  {alertData.alert_title}
                </Typography>
              </Box>

                {/* Contenu de l'alerte */}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.primary,
                  lineHeight: 1.5
                }}
              >
                {alertData.alert_content}
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>

    </Box>
  );
}

export default LogInPage;